/**
 * Marketing landing page.
 *
 * Pulls the value props directly from the product roadmap — three moats:
 * fix-don't-report, developer-first, agency-native. Top-of-fold is a
 * domain scan box that hits /scan-domain anonymously so visitors taste
 * the product before signing up.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
  ChevronRight,
  Loader2,
  ShieldCheck,
  ArrowRight,
  BookOpen,
  Search,
  Activity,
  BellRing,
  GitBranch,
  Check,
  X,
  Bot,
  Sparkles,
  Terminal,
} from 'lucide-react';
import { api, ApiHttpError } from '@/lib/api';
import { analytics } from '@/lib/analytics';
import { getTurnstileToken } from '@/lib/turnstile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { PostmasterMockup } from '@/components/landing/PostmasterMockup';
import { AlertEscalationMockup } from '@/components/landing/AlertEscalationMockup';
import { DnsAutofixMockup } from '@/components/landing/DnsAutofixMockup';
import { cn } from '@/lib/utils';

interface ScanResponse {
  domain: string;
  score: { total: number; grade: string };
  durationMs: number;
}

interface FeaturedGuide {
  slug: string;
  title: string;
  blurb: string;
}

const FEATURED_GUIDES: FeaturedGuide[] = [
  {
    slug: 'gmail-yahoo-bulk-sender-2026',
    title: 'Gmail & Yahoo bulk-sender requirements (2026 update)',
    blurb: 'What 5,000+ msgs/day senders must do: alignment, one-click unsubscribe, sub-0.30% spam rate.',
  },
  {
    slug: 'spf-setup',
    title: 'SPF setup & the 10-DNS-lookup limit',
    blurb: 'Authorize your senders without blowing the SPF lookup budget. The P0 every sender eventually hits.',
  },
  {
    slug: 'dmarc-rollout',
    title: 'A staged DMARC rollout (none → quarantine → reject)',
    blurb: 'Move from monitoring to enforcement without breaking legitimate mail. pct= ramps + RUA reads.',
  },
  {
    slug: 'fix-spf-permerror',
    title: 'Fixing SPF PermError (too many DNS lookups)',
    blurb: 'A PermError silently fails DMARC for every receiver. Diagnostic playbook + the fix.',
  },
];

export function LandingPage() {
  const [domain, setDomain] = useState('');
  const scan = useMutation<ScanResponse, ApiHttpError, string>({
    mutationFn: async (d) => {
      // Run the invisible Turnstile challenge before the API call. If the
      // env var isn't set client-side, getTurnstileToken returns null and
      // the API will skip server-side verification too.
      const turnstileToken = await getTurnstileToken('landing_scan').catch(() => null);
      return api.post<ScanResponse>(
        '/scan-domain',
        { domain: d, turnstileToken: turnstileToken ?? undefined },
        { noAuth: true },
      );
    },
    onSuccess: () => analytics.trackEvent('scan_run', { surface: 'landing' }),
  });

  return (
    <>
      <Helmet>
        <title>InboxGuard — Find &amp; fix email deliverability problems (MCP-first)</title>
        <meta
          name="description"
          content="The only platform an AI agent can both detect and remediate email-DNS problems with. Continuous SPF, DKIM, DMARC, BIMI, DNSSEC/DANE, and blocklist monitoring with one-click DNS fixes at your registrar and a production MCP server. 14-day free trial."
        />
        <link rel="canonical" href="https://inboxguard.io/" />
        <meta property="og:title" content="InboxGuard — Find and fix email deliverability problems" />
        <meta
          property="og:description"
          content="MCP-first email deliverability. Scan SPF, DKIM, DMARC, BIMI, and blocklists, then publish the DNS fix at your registrar. Claude, ChatGPT, or your own agent can run the whole loop."
        />
        <meta property="og:url" content="https://inboxguard.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://inboxguard.io/og-image.png" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <PublicHeader />

        {/* Hero — domain scan as the lead magnet */}
        <section className="hero-grad relative overflow-hidden">
          <div className="container mx-auto px-4 pb-24 pt-20 sm:pt-24">
            <div className="mx-auto max-w-3xl text-center animate-fade-in-up md:max-w-4xl lg:max-w-5xl">
              <span className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                <span>Free domain scan — no signup, results in ~3s</span>
              </span>

              {/* Headline width steps up at lg so the line "Every other
                  deliverability tool stops at the dashboard." doesn't ladder
                  awkwardly on wide monitors. text-balance keeps the wrap
                  symmetric on the breakpoints where it does still wrap.
                  Body copy below has its own max-w-2xl so it stays
                  readable-narrow regardless of what the headline does. */}
              <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
                Find <span className="bg-gradient-to-r from-primary to-emerald-400 bg-clip-text text-transparent">and fix</span>{' '}
                email deliverability problems.
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-balance text-lg text-muted-foreground">
                Every other tool stops at telling you what&apos;s broken. InboxGuard <em>finds</em> the
                problem and <em>fixes</em> it, publishing the exact DNS change at your registrar or
                handing the whole loop to an AI agent over MCP.
              </p>
            </div>

            {/* Free scan card — top-of-fold lead magnet, intentionally large. */}
            <div className="mx-auto mt-12 max-w-2xl animate-fade-in-up">
              <Card className="card-elev overflow-hidden border-primary/20 shadow-lg ring-1 ring-primary/10">
                <CardContent className="p-0">
                  <div className="flex items-center gap-2 border-b bg-muted/40 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Search className="h-3.5 w-3.5 text-primary" />
                    Free deliverability scan
                  </div>
                  <div className="p-5 sm:p-6">
                    <form
                      className="flex flex-col gap-2 sm:flex-row"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (domain) scan.mutate(domain.trim());
                      }}
                    >
                      <Input
                        placeholder="yourdomain.com"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        className="h-12 flex-1 text-base"
                        autoFocus
                      />
                      <Button
                        type="submit"
                        disabled={!domain || scan.isPending}
                        size="lg"
                        className="h-12 px-6 text-base"
                      >
                        {scan.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Scanning…
                          </>
                        ) : (
                          <>
                            Scan now
                            <ArrowRight className="ml-1 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </form>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <ShieldCheck className="h-3 w-3 text-success" />
                        Free, no signup
                      </span>
                      <span>·</span>
                      <span>SPF · DKIM · DMARC · MTA-STS · TLS-RPT · 11 blocklists</span>
                      <span>·</span>
                      <span>~3s avg</span>
                    </div>

                    {scan.data && (
                      <div className="mt-5 overflow-hidden rounded-md border bg-muted/30">
                        <div className="flex items-center justify-between gap-3 border-b bg-background px-4 py-3">
                          <span className="font-mono text-sm font-medium">{scan.data.domain}</span>
                          <span
                            className={cn(
                              'rounded-md px-2 py-1 font-mono text-sm font-semibold',
                              scoreBgClass(scan.data.score.total),
                            )}
                          >
                            {scan.data.score.total} / 100 · {scan.data.score.grade}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-xs">
                          <span className="text-muted-foreground">
                            Scanned in {scan.data.durationMs}ms
                          </span>
                          <Link
                            to="/auth?mode=sign-up"
                            className="inline-flex items-center font-medium text-primary hover:underline"
                          >
                            See the full breakdown, free
                            <ChevronRight className="h-3 w-3" />
                          </Link>
                        </div>
                      </div>
                    )}
                    {scan.error && (
                      <p className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {scan.error.message}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Trust strip — reduce sign-up anxiety with concrete proof. */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                <span>RFC-compliant probes</span>
                <span className="opacity-50">·</span>
                <span>Encrypted at rest &amp; in transit</span>
                <span className="opacity-50">·</span>
                <span>DNS-only — never touches your mail</span>
                <span className="opacity-50">·</span>
                <span>14-day no-card trial</span>
              </div>
            </div>
          </div>
        </section>

        {/* Three moats — concrete, with proof. Alternating left/right rows so
            the interactive mockups have room to breathe. */}
        <section className="border-y bg-muted/30 py-20">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
                Three things every other tool punts on.
              </h2>
              <p className="mt-3 text-muted-foreground">
                Anyone can run an SPF lookup. We close the loop from “something’s wrong” to “it’s fixed.”
              </p>
            </div>

            <div className="mt-16 space-y-20">
              <MoatRow
                icon={<Activity className="h-5 w-5" />}
                eyebrow="Postmaster integration"
                title="Live Google Postmaster sync"
                body="Connect once with Google OAuth. We pull reputation, spam rate, DKIM/SPF pass rates, and FBL volume from the daily Postmaster window, right into the same UI as your scans. No spreadsheet exports, no separate tab."
                bullets={[
                  'Reputation + spam-rate trend kept indefinitely',
                  'Daily pull, not on-demand. Runs while you sleep',
                  'One OAuth covers every domain on the Google account',
                ]}
                preview={<PostmasterMockup />}
              />
              <MoatRow
                reverse
                icon={<BellRing className="h-5 w-5" />}
                eyebrow="Team-grade alerting"
                title="Alerts that actually escalate"
                body="Email, Slack, and webhook delivery with per-severity routing. Critical alerts escalate to an on-call email and a backup channel if nobody resolves them in 30 minutes. Per-domain routing lets agencies run 50+ clients from one Slack workspace."
                bullets={[
                  'Email + Slack + webhooks, severity-routed',
                  'On-call style escalation timer per alert',
                  'Per-domain channel routing built for MSPs',
                ]}
                preview={<AlertEscalationMockup />}
              />
              <MoatRow
                icon={<GitBranch className="h-5 w-5" />}
                eyebrow="DNS auto-remediation"
                title="We apply the fix at your registrar"
                body="Connect your registrar and we compute the exact record change, show a diff against what's live, and apply it on your go. No tickets, no pasting TXT records into a portal at 11pm."
                bullets={[
                  'Diff preview before any change is applied',
                  'Cloudflare · Route 53 · GoDaddy · Namecheap',
                  'One-click rollback per change, audit log forever',
                ]}
                preview={<DnsAutofixMockup />}
              />
            </div>
          </div>
        </section>

        {/* Direct-comparison strip — why this is different from competitors */}
        <section className="container mx-auto max-w-5xl px-4 py-20">
          <div className="mx-auto max-w-2xl text-center md:max-w-3xl lg:max-w-4xl">
            {/* Same width-step pattern as the hero — opens the headline up on
                wide screens so "The deliverability tool you actually wanted."
                renders on a single line at lg+ and balanced 2 lines below. */}
            <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
              The deliverability tool you actually wanted.
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              How we stack up against the dashboard-only crowd, on the things that matter once you
              have more than one domain and a deadline.
            </p>
          </div>
          <div className="mt-10 overflow-hidden rounded-lg border bg-card">
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b bg-muted/40 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:grid-cols-[1fr_120px_140px] sm:px-6">
              <span></span>
              <span className="text-center text-primary">InboxGuard</span>
              <span className="text-center">Most others</span>
            </div>
            <ComparisonRow feature="Continuous SPF / DKIM / DMARC / MTA-STS / TLS-RPT / BIMI scans" us mostOthers />
            <ComparisonRow feature="11-blocklist DNSBL sweep on every scan" us mostOthers={false} />
            <ComparisonRow feature="Live Google Postmaster reputation + spam-rate trend" us mostOthers={false} />
            <ComparisonRow feature="Email + Slack alerting with on-call style escalation" us mostOthers={false} />
            <ComparisonRow feature="Apply the DNS fix at your registrar (one click, or via agent)" us mostOthers={false} />
            <ComparisonRow feature="MCP server an AI agent can scan AND remediate through" us mostOthers={false} />
            <ComparisonRow feature="Per-domain alert routing for agencies / MSPs" us mostOthers={false} />
            <ComparisonRow feature="Hosted DMARC RUA ingest with daily aggregates" us mostOthers />
            <ComparisonRow feature="REST API + signed webhooks + public report URLs" us mostOthers />
            <ComparisonRow feature="14-day no-card trial" us mostOthers />
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Not a paid placement, no kickbacks. If a competitor adds something we don&apos;t, we
            update this list.
          </p>
        </section>

        {/* What we check */}
        <section className="container mx-auto max-w-5xl px-4 py-20">
          <div className="max-w-2xl">
            <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">What we actually check</h2>
            <p className="mt-3 text-muted-foreground">
              The full authentication, transport-security, and reputation surface. Real
              RFC-compliant probes, no dummy data, no{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-sm">Math.random()</code> fallbacks.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {[
              ['SPF', '10-DNS-lookup limit enforced, PermError caught'],
              ['DKIM', 'Selector discovery, key length + algorithm hygiene'],
              ['DMARC', 'Policy, alignment, and RUA sender identification'],
              ['DMARC sources', 'Maps RUA source IPs to Mailchimp, SendGrid, SES…'],
              ['BIMI', 'Record, logo, and real VMC certificate validation'],
              ['MTA-STS', 'Policy fetch + enforcement mode'],
              ['TLS-RPT', 'Reporting endpoint + MX TLS health'],
              ['DNSSEC + DANE', 'DS/DNSKEY chain and TLSA records'],
              ['Blocklists', '11 DNSBLs, queried authoritative-side'],
            ].map(([k, v]) => (
              <div
                key={k}
                className="card-elev group rounded-lg border bg-card p-4 transition-colors hover:border-primary/40"
              >
                <div className="font-mono text-sm font-semibold tracking-wide text-primary">{k}</div>
                <div className="mt-1 text-xs text-muted-foreground">{v}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Plus live Google Postmaster reputation, hosted DMARC aggregate ingest, and{' '}
            <strong className="font-medium text-foreground">signed (HMAC-SHA256) outbound webhooks</strong>{' '}
            so your systems can trust every alert we send.
          </p>
        </section>

        {/* Agent-native / MCP — the differentiator that sends builders to the
            Developers hub. Kept tight; the full surface lives at /developers. */}
        <section className="border-y bg-muted/30 py-20">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-14">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                  <Bot className="h-3 w-3" />
                  MCP-first · agent-native
                </span>
                <h2 className="mt-4 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
                  Hand the whole loop to an AI agent.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  InboxGuard ships a production MCP server with 28 tools. Point Claude, ChatGPT,
                  Cursor, or your own agent at it, and it can scan a domain, read the score, plan
                  the fix, and publish the records, server-revalidated the whole way. It detects{' '}
                  <em>and</em> fixes, instead of only reporting.
                </p>
                <ul className="mt-6 space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>
                      The fix loop: <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">scan_domain</code>{' '}
                      → <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">get_dns_fix_plan</code>{' '}
                      → <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">apply_dns_fix</code>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>Open discovery; bearer API key for tool calls. REST + OpenAPI 3.1 too.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>Copy-paste configs for Claude Desktop, ChatGPT, Cursor, and Claude Code.</span>
                  </li>
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild>
                    <Link to="/developers">
                      Explore the Developers hub
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <a href="/agents.md" target="_blank" rel="noopener noreferrer">
                      Read agents.md
                    </a>
                  </Button>
                </div>
              </div>

              {/* Terminal-style snippet — concrete, on-brand, no JS needed. */}
              <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
                <div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2.5">
                  <Terminal className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Connect in one line
                  </span>
                </div>
                <pre className="overflow-x-auto bg-slate-900 px-4 py-4 font-mono text-xs leading-relaxed text-slate-100 dark:bg-slate-800">
                  <code>{`# Claude Code
claude mcp add --transport http inboxguard \\
  https://mcp.inboxguard.io/mcp \\
  --header "Authorization: Bearer ig_live_…"

# or hit it directly (no key, 5/hour per IP)
curl -sS -X POST https://api.inboxguard.io/scan-domain \\
  -H 'content-type: application/json' \\
  -d '{"domain":"example.com"}'`}</code>
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Featured guides */}
        <section className="border-t bg-muted/20 py-20">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                  <BookOpen className="h-3 w-3" />
                  Deliverability guides
                </span>
                <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
                  Learn the why before the fix.
                </h2>
                <p className="mt-3 text-muted-foreground">
                  Short, opinionated walkthroughs for the records and policies that actually decide whether
                  your mail lands in the inbox.
                </p>
              </div>
              <Button asChild variant="outline">
                <Link to="/guides">
                  All guides
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <FeaturedGuidesGrid />
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden border-t py-20">
          <div className="hero-grad absolute inset-0 -z-10" />
          <div className="container mx-auto max-w-3xl px-4 text-center">
            <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
              Start free. Upgrade when you outgrow it.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-balance text-muted-foreground">
              Free tier covers a single domain forever. Pro and Agency unlock continuous monitoring,
              hosted RUA reporting, and the full integration surface.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg">
                <Link
                  to="/auth?mode=sign-up"
                  onClick={() => analytics.trackCtaClick('signup', 'landing_footer')}
                >
                  Create free account
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link
                  to="/pricing"
                  onClick={() => analytics.trackCtaClick('see_pricing', 'landing_footer')}
                >
                  See pricing
                </Link>
              </Button>
              <Button asChild size="lg" variant="ghost">
                <Link
                  to="/developers"
                  onClick={() => analytics.trackCtaClick('developers', 'landing_footer')}
                >
                  For developers &amp; agents
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <PublicFooter />
      </div>
    </>
  );
}

/* DomainsClaimedBadge (a synthetic "~17,000 domains claimed" counter) was
 * removed 2026-07-12: it fabricated a specific, growing number with a fake live
 * pulse — deceptive-advertising exposure. The hero now uses a truthful static
 * value-prop badge. If real social proof is wanted, back it with a genuine
 * /stats/scan-count endpoint reading the actual public_scans count. */

/**
 * Full-width alternating moat row — text on one side, interactive mockup on
 * the other. `reverse` flips them so consecutive rows zigzag down the page.
 *
 * Uses CSS grid with explicit column ordering so we can rely on order classes
 * (rather than direction-rtl tricks) — keeps reading order correct for SR /
 * mobile (where everything stacks anyway).
 */
function MoatRow({
  icon,
  eyebrow,
  title,
  body,
  bullets,
  preview,
  reverse,
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  body: string;
  bullets?: string[];
  preview: React.ReactNode;
  reverse?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-14 lg:gap-20">
      <div className={cn('order-2 md:order-1', reverse && 'md:order-2')}>
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
          <span className="text-primary">{icon}</span>
          {eyebrow}
        </div>
        <h3 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">{title}</h3>
        <p
          className="mt-4 text-base leading-relaxed text-muted-foreground"
          // The body uses a single &gt; entity for the alert example. Render
          // it as HTML so the entity stays the original symbol after JSX
          // pretty-printing rather than the literal "&gt;" string.
          dangerouslySetInnerHTML={{ __html: body }}
        />
        {bullets && bullets.length > 0 && (
          <ul className="mt-6 space-y-2 text-sm">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={cn('order-1 md:order-2', reverse && 'md:order-1')}>
        {preview}
      </div>
    </div>
  );
}

function ComparisonRow({
  feature,
  us,
  mostOthers,
}: {
  feature: string;
  us: boolean;
  mostOthers: boolean;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b px-4 py-3 text-sm last:border-b-0 sm:grid-cols-[1fr_120px_140px] sm:px-6">
      <span>{feature}</span>
      <span className="flex items-center justify-center">
        {us ? (
          <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" aria-label="InboxGuard: yes" />
        ) : (
          <X className="h-5 w-5 text-muted-foreground/50" aria-label="InboxGuard: no" />
        )}
        <span className="sr-only">InboxGuard</span>
      </span>
      <span className="flex items-center justify-center text-xs text-muted-foreground">
        {mostOthers ? (
          <Check className="h-5 w-5 text-muted-foreground/60" aria-label="Most others: yes" />
        ) : (
          <X className="h-5 w-5 text-muted-foreground/30" aria-label="Most others: no" />
        )}
        <span className="sr-only">Most others</span>
      </span>
    </div>
  );
}

/**
 * The 4 featured guide cards on the landing page. Newest AI-published
 * guides float to the top; remaining slots are filled with the static
 * editor's-choice list. Always exactly 4 cards. AI-published cards
 * dedupe by slug against the static list to avoid showing the same guide
 * twice.
 */
function FeaturedGuidesGrid() {
  const q = useQuery<
    { items: Array<{ slug: string; title: string; blurb: string; publishedAt: string | null }> },
    ApiHttpError
  >({
    queryKey: ['guides-data', 'list'],
    queryFn: () => api.get('/guides-data'),
    retry: false,
  });

  const aiItems = q.data?.items ?? [];
  // Newest AI-published guides first (the API already returns them sorted
  // DESC by published_at). Cap at 4 — they completely replace the static
  // list when we have enough fresh content. Until then, fall back to the
  // hand-curated FEATURED_GUIDES so the section never goes empty.
  const seen = new Set<string>();
  const merged: FeaturedGuide[] = [];
  for (const ai of aiItems) {
    if (merged.length >= 4) break;
    if (seen.has(ai.slug)) continue;
    seen.add(ai.slug);
    merged.push({ slug: ai.slug, title: ai.title, blurb: ai.blurb });
  }
  for (const f of FEATURED_GUIDES) {
    if (merged.length >= 4) break;
    if (seen.has(f.slug)) continue;
    seen.add(f.slug);
    merged.push(f);
  }

  return (
    <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
      {merged.map((g) => (
        <Link
          key={g.slug}
          to={`/guides/${g.slug}`}
          className="card-elev group flex flex-col rounded-lg border bg-card p-5 transition-colors hover:border-primary/40"
        >
          <h3 className="text-base font-semibold tracking-tight group-hover:text-primary">
            {g.title}
          </h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{g.blurb}</p>
          <span className="mt-4 inline-flex items-center text-xs font-medium text-primary">
            Read guide
            <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </Link>
      ))}
    </div>
  );
}

function scoreBgClass(score: number): string {
  if (score >= 75) return 'bg-success/10 text-success';
  if (score >= 50) return 'bg-warning/15 text-warning';
  return 'bg-destructive/10 text-destructive';
}

export default LandingPage;
